import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, MessageCircle, Phone, ArrowRight, Camera, ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import LeadSection from "@/components/home/LeadSection";
import { getDistrict, getSettings, mediaUrl } from "@/lib/api";
import { useSettings, waLink, telLink, SITE_ORIGIN } from "@/lib/site";
import { rememberDistrict } from "@/lib/districtPreference";

export default function DistrictPage() {
  const { pageSlug } = useParams();
  const slug = (pageSlug || "").replace(/-dugun-fotografcisi$/, "");
  const settings = useSettings();
  const { data: d, isLoading, isError } = useQuery({
    queryKey: ["district", slug],
    queryFn: () => getDistrict(slug),
  });
  const { data: siteSettings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const stripItems = ((siteSettings && siteSettings.gallery_items) || []).slice(0, 6);
  const strip = stripItems.map((it) => mediaUrl(it.thumb_url || it.url));

  useEffect(() => {
    if (d && d.slug) rememberDistrict(d.slug);
  }, [d]);

  if (isLoading) {
    return <div className="min-h-screen grid place-items-center text-[#9A9387]">Yükleniyor...</div>;
  }
  if (isError || !d) {
    return (
      <div className="min-h-screen grid place-items-center text-center px-4">
        <div>
          <h1 className="font-display text-3xl text-[#F7F2E8]">İlçe bulunamadı</h1>
          <Link to="/" className="text-[#D7B86A] mt-4 inline-block">Anasayfaya dön</Link>
        </div>
      </div>
    );
  }

  const title = `${d.name} Düğün Fotoğrafçısı | Ankara Gelin Damat Çekimi`;
  const description = d.blog?.meta_description ||
    `${d.name} düğün fotoğrafçısı arayışındaki çiftlere özel: ${d.name} gelin damat dış çekim mekânları, ışık tavsiyeleri ve rezervasyon. Ankara düğün fotoğrafçısı.`;

  const districtFaq = [
    { q: `${d.name}'de dış çekim için en iyi mekânlar neler?`, a: `${d.name} için öne çıkan çekim noktaları: ${d.spots.join(", ")}. Konseptinize göre en uygun rotayı birlikte planlıyoruz.` },
    { q: `${d.name}'e geliyor musunuz?`, a: `Evet. Ankara'nın tüm ilçelerine olduğu gibi ${d.name}'e de ekipmanlarımızla geliyor, çekiminizi yerinde gerçekleştiriyoruz.` },
    { q: `Fotoğraflar ne zaman teslim ediliyor?`, a: `${d.name} çekiminizin ardından düzenlenmiş tüm kareleri 7 gün içinde dijital galeri linki ile teslim ediyoruz.` },
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org", "@type": "LocalBusiness",
      name: `${settings.brand_name} — ${d.name}`,
      url: `${SITE_ORIGIN}/ankara/${d.page_slug}`,
      telephone: settings.phone,
      image: d.image,
      areaServed: { "@type": "City", name: `${d.name}, Ankara` },
      address: { "@type": "PostalAddress", addressLocality: d.name, addressRegion: "Ankara", addressCountry: "TR" },
      geo: { "@type": "GeoCoordinates", latitude: d.lat, longitude: d.lng },
    },
    {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Anasayfa", item: SITE_ORIGIN },
        { "@type": "ListItem", position: 2, name: "Ankara İlçeleri", item: `${SITE_ORIGIN}/#ilceler` },
        { "@type": "ListItem", position: 3, name: `${d.name} Düğün Fotoğrafçısı` },
      ],
    },
    {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: districtFaq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    },
  ];

  return (
    <>
      <Seo title={title} description={description} path={`/ankara/${d.page_slug}`} image={d.image} jsonLd={jsonLd} />

      {/* Hero */}
      <section data-testid="district-hero" className="relative pt-28 sm:pt-32 lg:pt-40 pb-16 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={d.image} alt={`${d.name} düğün fotoğrafçısı`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0E0F10] via-[#0E0F10]/85 to-[#0E0F10]/60" />
        </div>
        <div className="container-lux relative">
          <Reveal>
            <nav className="text-sm text-[#B8B1A5] flex items-center gap-2">
              <Link to="/" className="hover:text-white">Anasayfa</Link>
              <span>/</span>
              <Link to="/#ilceler" className="hover:text-white">İlçeler</Link>
              <span>/</span>
              <span className="text-[#D7B86A]">{d.name}</span>
            </nav>
            <span className="eyebrow mt-5"><MapPin className="w-4 h-4" /> {d.region} · Ankara</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F7F2E8] mt-3 max-w-3xl leading-[1.05]">
              {d.name} Düğün Fotoğrafçısı
            </h1>
            <p className="text-[#CFC6B8] mt-5 max-w-2xl text-lg">{d.intro}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={waLink(settings, `Merhaba, ${d.name} için düğün çekimi hakkında bilgi almak istiyorum.`)}
                target="_blank" rel="noreferrer" data-testid="district-whatsapp-button">
                <Button size="lg" className="bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold">
                  <MessageCircle className="w-5 h-5 mr-2" /> WhatsApp'tan Yaz
                </Button>
              </a>
              <a href={telLink(settings)} data-testid="district-call-button">
                <Button size="lg" variant="outline" className="border-white/25 text-[#F7F2E8] bg-transparent hover:bg-white/5">
                  <Phone className="w-5 h-5 mr-2" /> Hemen Ara
                </Button>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Spots + gallery strip */}
      <section className="section-pad bg-[#141517]">
        <div className="container-lux grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <Reveal>
            <div>
              <span className="eyebrow"><Camera className="w-4 h-4" /> Çekim Noktaları</span>
              <h2 className="font-display text-3xl sm:text-4xl text-[#F7F2E8] mt-3">
                {d.name}'de öne çıkan lokasyonlar
              </h2>
              <p className="text-[#9A9387] mt-4">{d.tagline}. Konseptinize göre aşağıdaki noktalardan bir rota oluşturuyoruz.</p>
              <ul className="grid sm:grid-cols-2 gap-3 mt-6">
                {d.spots.map((s) => (
                  <li key={s} className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-[#1A1B1E] px-4 py-3">
                    <MapPin className="w-4 h-4 text-[#C8A24A] shrink-0" />
                    <span className="text-sm text-[#EDE7DA]">{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-3">
              {strip.map((src, i) => (
                <img key={i} src={src} alt={`${d.name} düğün fotoğrafı ${i + 1}`} loading="lazy"
                  className={`w-full object-cover rounded-xl border border-white/8 ${i % 3 === 0 ? "h-56" : "h-40"}`} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Blog link */}
      {d.blog && (
        <section className="py-14 bg-[#0E0F10]">
          <div className="container-lux">
            <Link to={`/blog/${d.blog.slug}`}
              className="group flex flex-col sm:flex-row items-start sm:items-center gap-5 rounded-2xl bg-[#FBF7EF] p-6 sm:p-8 border border-black/5">
              <img src={d.blog.hero_image} alt={d.blog.title} className="w-full sm:w-48 h-40 sm:h-32 object-cover rounded-xl" />
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.14em] text-[#9a7b23]">Rehber Yazı</div>
                <h3 className="font-display text-2xl text-[#141517] mt-1">{d.blog.title}</h3>
                <p className="text-sm text-[#5B5F66] mt-1.5 line-clamp-2">{d.blog.excerpt}</p>
              </div>
              <span className="inline-flex items-center gap-1 text-[#9a7b23] font-medium group-hover:gap-2 transition-all">
                Oku <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </section>
      )}

      {/* District FAQ */}
      <section className="section-pad bg-[#141517]">
        <div className="container-lux max-w-3xl">
          <h2 className="font-display text-3xl sm:text-4xl text-[#F7F2E8] text-center">{d.name} · Sık Sorulanlar</h2>
          <Accordion type="single" collapsible className="mt-8">
            {districtFaq.map((f, i) => (
              <AccordionItem key={i} value={`d-${i}`} className="border-white/10">
                <AccordionTrigger className="text-left text-[#EDE7DA] hover:text-white py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#9A9387] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Nearby districts */}
      {d.nearby_full?.length > 0 && (
        <section className="py-14 bg-[#0E0F10]">
          <div className="container-lux">
            <h2 className="font-display text-2xl text-[#F7F2E8] mb-6">Yakın ilçeler</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {d.nearby_full.map((n) => (
                <Link key={n.slug} to={`/ankara/${n.page_slug}`} data-testid="district-card-link"
                  className="group flex items-center justify-between rounded-xl border border-white/10 bg-[#141517] px-4 py-3.5 hover:border-[rgba(200,162,74,0.5)] transition-colors">
                  <span className="text-sm text-[#EDE7DA]">{n.name}</span>
                  <ArrowUpRight className="w-4 h-4 text-[#7C766B] group-hover:text-[#C8A24A]" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <LeadSection presetDistrict={d.slug} title={`${d.name}'de çekim için tarih alın`}
        subtitle={`${d.name} ve çevresi için uygun tarihleri ve paket fiyatlarını paylaşalım.`} />
    </>
  );
}
